import Group16 from '@carbon/icons-react/es/group/16';
import User16 from '@carbon/icons-react/es/user/16';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { View } from 'api/views/types';
import { Loader } from 'components/organisms/projects/project-sidebar/spaces-menu/styles';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useIsExternalUser } from 'hooks/use-user-type';
import { sortByText } from 'utils/sort-by-text';
import { asList } from '../core';
import { Views } from '../types';
import { Menu } from './components';
import { renderViewList } from './view-list';

interface Props<ViewDefinition = any> {
  views: Views<ViewDefinition>;
  defaultViewsList: Views<ViewDefinition>;
  defaultViewId: number;
  selectedView?: View<ViewDefinition>;
  loading: boolean;
  onViewEdit: (view: View<ViewDefinition>) => void;
  onViewDelete: (view: View<ViewDefinition>) => void;
  onViewSelect: (view: View<ViewDefinition>, isClearAll?: boolean) => void;
  searchText?: string;
}

const StyledMenu = styled(Menu)`
  &:first-child {
    margin-top: var(--spacing-05);
  }
`;

const IconShell = styled.span`
  color: var(--icon-primary);
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const AllViews: FC<Props> = ({
  defaultViewId,
  defaultViewsList,
  views,
  loading,
  selectedView,
  onViewSelect,
  onViewEdit,
  onViewDelete,
  searchText,
}) => {
  const translate = useTranslate();
  const {
    user: { userId: currentUserId },
  } = useUser();
  const sortedViews = useMemo(
    () =>
      asList(views).sort(([, { viewName: a }], [, { viewName: b }]) =>
        sortByText(a, b),
      ),
    [views],
  );
  const mySavedViews = useMemo(
    () => sortedViews.filter(([, x]) => x.createdBy === currentUserId),
    [sortedViews, currentUserId],
  );
  const sharedViews = useMemo(
    () => sortedViews.filter(([, x]) => x.createdBy !== currentUserId),
    [sortedViews, currentUserId],
  );

  const searchedViews = useMemo(() => {
    if (!searchText) {
      return sortedViews;
    }

    return sortedViews.filter(([, x]) =>
      x.viewName.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [sortedViews, searchText]);

  const currentUserPermissionSetType = useUserPermissionSetType();
  const isExternalUser = useIsExternalUser();
  const { projectId } = useParams<{ projectId: string }>();

  return (
    <StyledMenu
      mode="inline"
      defaultOpenKeys={['mySavedViews', 'sharedViews', 'searchResult']}
      defaultSelectedKeys={[defaultViewId.toString()]}
      selectedKeys={[selectedView ? selectedView.viewId.toString() : '']}
    >
      {searchText ? (
        renderViewList({
          icon: <Group16 />,
          title: (
            <FormattedMessage
              id="views.searched_views.title"
              values={{ count: searchedViews.length }}
            />
          ),
          menuKey: 'searchResult',
          loadingMessage: <FormattedMessage id="loading" />,
          emptyMessage: <FormattedMessage id="views.searched_views.empty" />,
          views: searchedViews,
          loading,
          currentUserId,
          onViewSelect,
          onViewEdit,
          onViewDelete,
          currentUserPermissionSetType,
          isExternalUser,
          isProjectTaskList: projectId,
        })
      ) : (
        <>
          {defaultViewsList.ids[0] === 'loading' ? (
            <Loader>
              <FormattedMessage id="loading" />
            </Loader>
          ) : (
            asList(defaultViewsList).map(([, view]) => {
              return (
                <Menu.Item
                  data-cy={view.viewName}
                  icon={<IconShell>{view.viewIcon}</IconShell>}
                  key={view.viewId}
                  onClick={() => onViewSelect(view)}
                >
                  {isExternalUser &&
                  view.viewName === translate('all_tasks.filters.all_tasks')
                    ? translate('all_tasks')
                    : view.viewName}
                </Menu.Item>
              );
            })
          )}
          {!isExternalUser &&
            renderViewList({
              icon: <User16 />,
              title: <FormattedMessage id="views.saved_views.title" />,
              menuKey: 'mySavedViews',
              loadingMessage: <FormattedMessage id="loading" />,
              emptyMessage: <FormattedMessage id="views.saved_views.empty" />,
              views: mySavedViews,
              loading,
              currentUserId,
              onViewSelect,
              onViewEdit,
              onViewDelete,
              currentUserPermissionSetType,
              isExternalUser,
              isProjectTaskList: projectId,
            })}
          {renderViewList({
            icon: <Group16 />,
            title: <FormattedMessage id="views.shared_views.title" />,
            menuKey: 'sharedViews',
            loadingMessage: <FormattedMessage id="loading" />,
            emptyMessage: <FormattedMessage id="views.shared_views.empty" />,
            views: sharedViews,
            loading,
            currentUserId,
            onViewSelect,
            onViewEdit,
            onViewDelete,
            currentUserPermissionSetType,
            isExternalUser,
            isProjectTaskList: projectId,
          })}
        </>
      )}
    </StyledMenu>
  );
};
